<script setup lang="ts">
import {ref} from "vue";
import {Button} from "@/Components/Library";
import {onLongPress} from "@vueuse/core";
import {useToast} from "primevue/usetoast";
import useAudioRecorder from "@/Composables/audioRecorder";

const model = defineModel();
const toast = useToast();

const recordButton = ref<HTMLElement | null>(null);

const {
    recording,
    paused,
    finished,
    playing,
    recordingProgress,
    playbackProgress,
    audioFile,
    audioUrl,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    playRecording,
    pausePlayback,
    deleteRecording,
} = useAudioRecorder({
    enablePauseResume: true,
    enableProgressTracking: true,
    sizeLimit: null, // 500 * 1048576 if needed
    timeLimit: null,
    fileNamingStrategy: "random",
    onRecordingStart: () => {
        console.log("start recording");
    },
    onRecordingStop: () => {
        if (audioFile.value) {
            model.value = audioFile.value;
        }
    },
    onRecordingError: (error) => {
        console.log(error);
    },
});

const saveRecording = () => {
    if (audioFile.value) {
        model.value = audioFile.value;
    }
};

onLongPress(recordButton, startRecording, {
    delay: 500,
    onMouseUp: (duration, distance, isLongPress) => {
        if (!isLongPress && !recording.value) {
            toast.add({
                severity: "warn",
                summary: "Voice recording",
                detail: "Long press to start recording",
                life: 3000,
            });
        }
        if (!isLongPress && recording.value) {
            if (!paused.value) {
                pauseRecording();
            } else {
                resumeRecording();
            }
        }
    },
});

defineExpose({
    stopRecording,
    saveRecording,
    deleteRecording,
});
</script>
<template>
    <div class="flex flex-col items-center">
        <div class="flex items-center justify-center py-4">
            <Button
                ref="recordButton"
                :icon="{
                    icon: recording && !paused ? 'mdi:pause' : 'mynaui:microphone',
                    width: 72,
                }"
                class="!px-4 !py-4 text-white"
                :class="[recording ? 'bg-red-600' : 'bg-askr-green']"
            />
        </div>
        <div class="flex items-center justify-center gap-2 pb-2">
            <Button
                :icon="{icon: playing ? 'mdi:pause' : 'mdi:play', width: 32}"
                :disabled="recording || !finished"
                class="bg-askr-green !px-2 !py-2 text-white"
                @click="playing ? pausePlayback() : playRecording()"
            />
            <Button
                :icon="{icon: 'mdi:stop', width: 32}"
                :disabled="!recording && !playing"
                class="bg-stone-700 !px-2 !py-2 text-white"
                @click="stopRecording"
            />
            <a :href="audioUrl" download>
                <Button
                    :icon="{icon: 'mdi:download', width: 32}"
                    :disabled="!audioUrl || recording || playing"
                    class="bg-askr-green !px-2 !py-2 text-white"
                />
            </a>
        </div>
    </div>
</template>
<style scoped></style>
